import { lazy } from 'react';
import {
  HomeOutlined,
  CheckSquareOutlined,
  OrderedListOutlined,
  FileTextOutlined,
  CopyOutlined,
  UsergroupAddOutlined,
  RedEnvelopeOutlined,
  FileSyncOutlined,
  UserOutlined,
  DatabaseOutlined,
  DollarCircleOutlined,
  DesktopOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';
import { t } from 'helpers/i18n';
import {
  resourceConstants,
  appConstants,
  actionConstants,
  localStorageConstants,
} from 'constants/index';

const {
  ASSIGN_IMPLEMENT,
  CREATE_IMPLEMENT_REQUEST,
  LIST_IMPLEMENT,
  REPORT_ADMIN_REGIONAL,
  REPORT_APPROACH,
  REPORT_DAILY,
  REPORT_SHOPQR,
  APPROVAL_TERMINAL,
  JOBS_ADMIN,
  PROGRESS_REPORT_VIEW,
  TICKET_ISSUES,
  TICKET_REPORT,
  SHOP_TAKE_CARE,
  SPOS,
  HUMAN_RESOURCE_MANAGEMENT,
  WAITING_GRANTED_BANK,
  TIMEKEEPING,
  FILE_IMPORT_LEAD,
  REPORT_SHOP_QR_SALE,
  REPORT_SHOP_TAKE_CARE_SALE,
  REPORT_SPOS_SALE,
  CALL_CENTER_STAFF,
} = resourceConstants;

const {
  SALE_PORTAL_BFF,
  TICKET_BFF_SERVICE,
  ATTRIBUTE_PROMO,
  TIMESHEET_BFF,
  LEAD_BFF,
} = appConstants;

const { COMPANY_ID } = localStorageConstants;

const { READ, CREATE, VIEW_DETAIL, VIEW_LIST_SPOS } = actionConstants;
const permission = (app: string, resource: string, action?: string) => ({
  app,
  resource,
  action,
});

// App pages
const Home = lazy(() => import('containers/Home'));
const LoginSuccess = lazy(() => import('containers/LoginSuccess'));

// const Shop = lazy(() => import('containers/Shop/ListShop'));
const AssignSaleShop = lazy(() =>
  import('containers/Implement/AssignTask/AssignForm')
);
const AssignTask = lazy(() =>
  import('containers/Implement/AssignTask/AssignTask')
);
// const Task = lazy(() => import('containers/Implement/Task'));
const TaskDetail = lazy(() => import('containers/Report/TaskDetail'));

const Tasks = lazy(() => import('containers/Report/Tasks'));
const CustomerAccess = lazy(() => import('containers/Mobile/StepMerchant'));
const SelectService = lazy(() => import('containers/Mobile/SelectService'));
const StepMerchant = lazy(() => import('containers/Mobile/StepMerchant'));
const CreateMerchant = lazy(() => import('containers/Shop/CreateMerchant'));
const Mobile = lazy(() => import('containers/Mobile/Layout'));
const CreateImplement = lazy(() => import('containers/CreateImplement'));
const ListImplement = lazy(() => import('containers/ListImplement'));
const WaitingGrantedBank = lazy(() => import('containers/WaitingGrantedBank'));
const ApproachingShop = lazy(() =>
  import('containers/Report/Approaching/Shop')
);
const ApproachingLead = lazy(() =>
  import('containers/Report/Approaching/Lead')
);
const NewShopList = lazy(() => import('containers/Report/NewShopList'));
const ApproveTerminal = lazy(() => import('containers/ApproveTerminal'));
const ListShopTakeCare = lazy(() =>
  import('containers/Report/ListShopTakeCare')
);
const ProgressReport = lazy(() => import('containers/Report/Progress'));
const ProgressReportDetail = lazy(() =>
  import('containers/Report/Progress/ApproachHistory')
);
//regional admin
const RegionalAdmin = lazy(() => import('containers/RegionalAdmin'));
//daily report
const DailyReport = lazy(() => import('containers/Report/DailyReport'));
//promotion request
const PromotionRequest = lazy(() => import('containers/PromotionRequest'));
// job admin
const JobsAdmin = lazy(() => import('containers/JobsAdmin'));

//Iframe
const IFrameContainer = lazy(() =>
  import('containers/IFrameContainer/IFrameContainer')
);
// List
const ListSPOS = lazy(() => import('containers/ListSPOS'));
const SPOSDetail = lazy(() => import('containers/SposDetail'));
const ReportTimesheet = lazy(() => import('containers/Timesheet/Report'));

// Report of sale
const ShopsQr = lazy(() => import('containers/Mobile/NewShopList'));
const ShopsTakeCare = lazy(() => import('containers/Mobile/ListShopTakeCare'));
const SposContracts = lazy(() => import('containers/Mobile/SposContractList'));
/*
 * If route has children, it's a parent menu (not link to any pages)
 * You can change permissions to your IAM's permissions
 */
const routes = [
  // This is a menu/route which has no children (sub-menu)
  {
    exact: true,
    path: '/',
    name: t('Home'),
    component: Home,
    icon: HomeOutlined,
  },

  {
    exact: true,
    path: '/shop/create-merchant',
    component: CreateMerchant,
  },
  // ticket management
  {
    exact: true,
    path: '/ticket-management',
    name: t('TicketManagement'),
    icon: DesktopOutlined,
    children: [
      '/ticket-management/waiting',
      '/ticket-management/approaching-customer',
    ],
    companies: [window.COMPANY_ID_DIGILIFE],
  },
  {
    exact: true,
    path: '/ticket-management/approaching-customer',
    name: t('CustomerAccess'),
    component: CustomerAccess,
    companies: [window.COMPANY_ID_DIGILIFE],
  },
  {
    exact: true,
    path: '/ticket-management/waiting',
    name: t('TicketWaiting'),
    component: Tasks,
    companies: [window.COMPANY_ID_DIGILIFE],
  },
  {
    exact: true,
    path: '/ticket-management/waiting/:id',
    name: t('TaskDetail'),
    component: TaskDetail,
    companies: [window.COMPANY_ID_DIGILIFE],
  },

  // implement management
  {
    exact: true,
    path: '/task-management',
    name: t('ImplementManagement'),
    icon: OrderedListOutlined,
    children: [
      '/implement/assign-sale-shop',
      '/implement/create',
      '/implement/list',
      '/implement/assign-task',
    ],
    permissions: [
      permission(SALE_PORTAL_BFF, CREATE_IMPLEMENT_REQUEST, CREATE),
      permission(SALE_PORTAL_BFF, ASSIGN_IMPLEMENT, READ),
      permission(SALE_PORTAL_BFF, LIST_IMPLEMENT, READ),
    ],
  },

  {
    exact: true,
    path: '/implement/create',
    name: t('Implement.CreateImplement'),
    component: CreateImplement,
    permissions: [
      permission(SALE_PORTAL_BFF, CREATE_IMPLEMENT_REQUEST, CREATE),
    ],
    companies: [window.COMPANY_ID_VNNG],
  },
  {
    exact: true,
    path: '/implement/assign-sale-shop',
    name: t('AssignTask'),
    component: AssignSaleShop,
    permissions: [permission(SALE_PORTAL_BFF, ASSIGN_IMPLEMENT, READ)],
    companies: [window.COMPANY_ID_VNNG, window.COMPANY_ID_DIGILIFE],
  },
  {
    exact: true,
    path: '/implement/assign-sale-shop/:id',
    name: t('AssignTask'),
    component: AssignSaleShop,
  },
  {
    exact: true,
    path: '/implement/assign-task',
    name: t('AssignTask'),
    component: AssignTask,
    companies: [window.COMPANY_ID_CRM_TEKO],
  },
  {
    exact: true,
    path: '/implement/list',
    name: t('Implement.ListImplement'),
    component: ListImplement,
    permissions: [permission(SALE_PORTAL_BFF, LIST_IMPLEMENT, READ)],
  },

  {
    exact: true,
    path: '/spos/:id',
    component: SPOSDetail,
    permissions: [permission(SALE_PORTAL_BFF, SPOS, VIEW_DETAIL)],
  },

  {
    exact: true,
    path: '/report/task/:id',
    name: 'Mobile',
    component: TaskDetail,
  },
  {
    exact: true,
    path: '/report',
    name: t('Report'),
    icon: FileTextOutlined,
    children: [
      '/report/lead-approaching',
      '/report/approaching',
      '/report/new-shops',
      '/report/daily',
      '/report/take-care',
      '/report/progress',
      '/report/shops-qr',
      '/report/shops-take-care',
      '/report/spos-contracts',
    ],
    permissions: [
      permission(SALE_PORTAL_BFF, REPORT_APPROACH, READ),
      permission(SALE_PORTAL_BFF, REPORT_SHOPQR, READ),
      permission(SALE_PORTAL_BFF, REPORT_DAILY, READ),
      permission(SALE_PORTAL_BFF, PROGRESS_REPORT_VIEW, READ),
      permission(SALE_PORTAL_BFF, REPORT_SHOP_QR_SALE, READ),
      permission(SALE_PORTAL_BFF, REPORT_SHOP_TAKE_CARE_SALE, READ),
      permission(SALE_PORTAL_BFF, REPORT_SPOS_SALE, READ),
    ],
  },
  {
    exact: true,
    path: '/report/lead-approaching',
    name: t('LeadApproaching'),
    component: ApproachingLead,
    permissions: [permission(SALE_PORTAL_BFF, REPORT_APPROACH, READ)],
  },
  {
    exact: true,
    path: '/report/approaching',
    name: t('ApproachingReport'),
    component: ApproachingShop,
    permissions: [permission(SALE_PORTAL_BFF, REPORT_APPROACH, READ)],
  },
  {
    exact: true,
    path: '/report/new-shops',
    name: t('NewShop.Root'),
    component: NewShopList,
    permissions: [permission(SALE_PORTAL_BFF, REPORT_SHOPQR, READ)],
    companies: [window.COMPANY_ID_VNNG, window.COMPANY_ID_DIGILIFE],
  },
  {
    exact: true,
    path: '/report/daily',
    name: t('DailyReport.Root'),
    component: DailyReport,
    permissions: [permission(SALE_PORTAL_BFF, REPORT_DAILY, READ)],
    companies: [window.COMPANY_ID_VNNG],
  },
  {
    exact: true,
    path: '/report/take-care',
    name: t('ListShopTakeCare.Root'),
    component: ListShopTakeCare,
    permissions: [permission(SALE_PORTAL_BFF, SHOP_TAKE_CARE, READ)],
    companies: [window.COMPANY_ID_VNNG],
  },
  {
    exact: true,
    path: '/report/progress',
    name: t('ProgressReport.Root'),
    component: ProgressReport,
    permissions: [permission(SALE_PORTAL_BFF, PROGRESS_REPORT_VIEW, READ)],
    companies: [window.COMPANY_ID_DIGILIFE],
  },
  {
    exact: true,
    path: '/report/progress/:id',
    component: ProgressReportDetail,
    permissions: [permission(SALE_PORTAL_BFF, PROGRESS_REPORT_VIEW, READ)],
  },
  {
    exact: true,
    path: '/report/shops-qr',
    name: t('NewShop.Root'),
    component: ShopsQr,
    permissions: [permission(SALE_PORTAL_BFF, REPORT_SHOP_QR_SALE, READ)],
    companies: [window.COMPANY_ID_DIGILIFE],
  },
  {
    exact: true,
    path: '/report/shops-take-care',
    name: t('ListShopTakeCare.Root'),
    component: ShopsTakeCare,
    permissions: [
      permission(SALE_PORTAL_BFF, REPORT_SHOP_TAKE_CARE_SALE, READ),
    ],
    companies: [window.COMPANY_ID_DIGILIFE],
  },
  {
    exact: true,
    path: '/report/spos-contracts',
    name: t('Spos.ListSPOS'),
    component: SposContracts,
    permissions: [permission(SALE_PORTAL_BFF, REPORT_SPOS_SALE, READ)],
    companies: [window.COMPANY_ID_DIGILIFE],
  },

  {
    path: '/ticket/report/status',
    name: t('Ticket.ReportByStatus'),
    component: IFrameContainer,
    iFrameSrc: `${window.REACT_APP_TICKET_UI}/report/status`,
    permissions: [permission(TICKET_BFF_SERVICE, TICKET_REPORT, READ)],
  },
  {
    exact: true,
    path: '/ticket/report/time',
    name: t('Ticket.ReportByTime'),
    component: IFrameContainer,
    iFrameSrc: `${window.REACT_APP_TICKET_UI}/report/time`,
    permissions: [permission(TICKET_BFF_SERVICE, TICKET_REPORT, READ)],
  },
  {
    exact: true,
    path: '/ticket/report/channel',
    name: t('Ticket.ReportByChannel'),
    component: IFrameContainer,
    iFrameSrc: `${window.REACT_APP_TICKET_UI}/report/channel`,
    permissions: [permission(TICKET_BFF_SERVICE, TICKET_REPORT, READ)],
  },
  {
    exact: true,
    path: '/ticket/report/daily',
    name: t('Ticket.DailyReport'),
    component: IFrameContainer,
    iFrameSrc: `${window.REACT_APP_TICKET_UI}/report/daily`,
    permissions: [permission(TICKET_BFF_SERVICE, TICKET_REPORT, READ)],
  },
  {
    exact: true,
    path: '/ticket/report/summary',
    name: t('Ticket.SummaryReport'),
    component: IFrameContainer,
    iFrameSrc: `${window.REACT_APP_TICKET_UI}/report/summary`,
    permissions: [permission(TICKET_BFF_SERVICE, TICKET_REPORT, READ)],
  },
  {
    exact: true,
    path: '/select-service',
    name: t('SelectService'),
    component: SelectService,
  },
  {
    exact: true,
    path: '/customer-access',
    name: t('CustomerAccess'),
    component: StepMerchant,
  },
  {
    exact: true,
    path: '/mobile',
    name: 'Mobile',
    component: Mobile,
  },
  {
    exact: true,
    path: '/loginSuccess',
    name: 'loginSuccess',
    component: LoginSuccess,
  },
  {
    exact: true,
    path: '/approval-terminal',
    name: t('ApproveTerminal.Root'),
    component: ApproveTerminal,
    icon: CopyOutlined,
    permissions: [permission(SALE_PORTAL_BFF, APPROVAL_TERMINAL, READ)],
    companies: [window.COMPANY_ID_VNNG],
  },

  {
    exact: true,
    path: '/management-spos',
    name:
      localStorage.getItem(COMPANY_ID) === window.COMPANY_ID_NAB.toString()
        ? t('Spos.ListSPOS')
        : t('Spos.SPOSAppraisal'),
    icon: DollarCircleOutlined,
    children: ['/spos', '/waiting-granted-bank'],
    permissions: [permission(SALE_PORTAL_BFF, SPOS, VIEW_LIST_SPOS)],
  },
  {
    exact: true,
    path: '/spos',
    name:
      localStorage.getItem(COMPANY_ID) === window.COMPANY_ID_NAB.toString()
        ? t('Spos.ListSPOS')
        : t('Spos.SPOSAppraisal'),
    component: ListSPOS,
    permissions: [permission(SALE_PORTAL_BFF, SPOS, VIEW_LIST_SPOS)],
  },
  {
    exact: true,
    path: '/waiting-granted-bank',
    name: t('WaitingGrantedBank.Root'),
    component: WaitingGrantedBank,
    permissions: [permission(SALE_PORTAL_BFF, WAITING_GRANTED_BANK, READ)],
    companies: [window.COMPANY_ID_VNNG],
  },

  {
    path: '/promotion-request',
    name: t('PromotionRequest.Root'),
    icon: RedEnvelopeOutlined,
    component: PromotionRequest,
    attributes: [ATTRIBUTE_PROMO],
    companies: [window.COMPANY_ID_VNNG],
  },

  {
    exact: true,
    path: '/ticket',
    name: 'Ticket',
    icon: CheckSquareOutlined,
    children: ['/ticket/list', '/ticket/create', '/ticket/report'],
    permissions: [
      permission(TICKET_BFF_SERVICE, TICKET_ISSUES, READ),
      permission(TICKET_BFF_SERVICE, TICKET_ISSUES, CREATE),
      permission(TICKET_BFF_SERVICE, TICKET_REPORT, READ),
    ],
  },
  {
    exact: true,
    path: '/ticket/list',
    name: t('Ticket.TicketList'),
    component: IFrameContainer,
    iFrameSrc: `${window.REACT_APP_TICKET_UI}/tickets/list`,
    permissions: [permission(TICKET_BFF_SERVICE, TICKET_ISSUES, READ)],
  },
  {
    exact: true,
    path: '/ticket/create',
    name: t('Ticket.CreateTicket'),
    component: IFrameContainer,
    iFrameSrc: `${window.REACT_APP_TICKET_UI}/tickets/create`,
    permissions: [permission(TICKET_BFF_SERVICE, TICKET_ISSUES, CREATE)],
  },
  {
    exact: true,
    path: '/ticket/report',
    name: t('Report'),
    children: [
      '/ticket/report/status',
      '/ticket/report/time',
      '/ticket/report/channel',
      '/ticket/report/daily',
      '/ticket/report/summary',
    ],
    permissions: [permission(TICKET_BFF_SERVICE, TICKET_REPORT, READ)],
  },

  {
    exact: true,
    path: '/orgchart/',
    name: t('HROrgchart.HumanResourceManagement'),
    icon: UserOutlined,
    children: [
      '/orgchart/user-profiles',
      '/orgchart/teams',
      '/orgchart/departments',
      '/orgchart/report-line',
    ],
    permissions: [permission(SALE_PORTAL_BFF, HUMAN_RESOURCE_MANAGEMENT, READ)],
  },
  {
    exact: true,
    path: '/orgchart/user-profiles',
    name: t('HROrgchart.EmployeeManagement'),
    component: IFrameContainer,
    iFrameSrc: `${window.REACT_APP_HR_ORGCHART_UI}/management/user-profiles`,
    permissions: [permission(SALE_PORTAL_BFF, HUMAN_RESOURCE_MANAGEMENT, READ)],
  },
  {
    exact: true,
    path: '/orgchart/teams',
    name: t('HROrgchart.TeamManagement'),
    component: IFrameContainer,
    iFrameSrc: `${window.REACT_APP_HR_ORGCHART_UI}/management/teams`,
    permissions: [permission(SALE_PORTAL_BFF, HUMAN_RESOURCE_MANAGEMENT, READ)],
  },
  {
    exact: true,
    path: '/orgchart/departments',
    name: t('HROrgchart.DepartmentManagement'),
    component: IFrameContainer,
    iFrameSrc: `${window.REACT_APP_HR_ORGCHART_UI}/management/departments`,
    permissions: [permission(SALE_PORTAL_BFF, HUMAN_RESOURCE_MANAGEMENT, READ)],
  },
  {
    exact: true,
    path: '/orgchart/report-line',
    name: t('HROrgchart.Reportline'),
    component: IFrameContainer,
    iFrameSrc: `${window.REACT_APP_HR_ORGCHART_UI}/management/report-line`,
    permissions: [permission(SALE_PORTAL_BFF, HUMAN_RESOURCE_MANAGEMENT, READ)],
  },

  {
    path: '/regional-admin',
    name: t('SetupRegionalAdmin.Root'),
    icon: UsergroupAddOutlined,
    component: RegionalAdmin,
    permissions: [permission(SALE_PORTAL_BFF, REPORT_ADMIN_REGIONAL, READ)],
    companies: [window.COMPANY_ID_VNNG],
  },

  {
    exact: true,
    path: '/timesheet/',
    name: t('Timesheet.Root'),
    icon: DatabaseOutlined,
    children: ['/timesheet/report'],
    permissions: [permission(TIMESHEET_BFF, TIMEKEEPING, READ)],
  },
  {
    exact: true,
    path: '/timesheet/report',
    name: t('Timesheet.Report.Root'),
    component: ReportTimesheet,
    permissions: [permission(TIMESHEET_BFF, TIMEKEEPING, READ)],
  },

  {
    path: '/jobs-admin',
    name: 'Qlý CronJobs - Chỉ sd bởi Dev',
    icon: FileSyncOutlined,
    component: JobsAdmin,
    permissions: [permission(SALE_PORTAL_BFF, JOBS_ADMIN, READ)],
  },
  {
    exact: true,
    path: '/leads',
    name: t('PotentialCustomers'),
    icon: CustomerServiceOutlined,
    children: ['/leads/file-import-lead', '/leads/list'],
    permissions: [
      permission(LEAD_BFF, FILE_IMPORT_LEAD, READ),
      permission(TICKET_BFF_SERVICE, CALL_CENTER_STAFF, READ),
    ],
  },
  {
    exact: true,
    path: '/leads/file-import-lead',
    name: t('FileImportLead.Title'),
    component: IFrameContainer,
    iFrameSrc: `${window.REACT_APP_LM_UI}/file-import-lead`,
    permissions: [permission(LEAD_BFF, FILE_IMPORT_LEAD, READ)],
  },
  {
    exact: true,
    path: '/leads/list',
    name: t('PotentialCustomers'),
    component: IFrameContainer,
    iFrameSrc: `${window.REACT_APP_TICKET_UI}/leads/list`,
    permissions: [permission(TICKET_BFF_SERVICE, CALL_CENTER_STAFF, READ)],
    companies: [window.COMPANY_ID_DIGILIFE, window.COMPANY_ID_VNNG],
  },
];

export default routes;
